import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../lib/supabase';

import '../styles/ProjectManager.css';

const ProjectManager = ({ 
  calculatorState, 
  onLoadProject,
  onProjectAction,
  isSubscribed = false,
  showSaveForm: propShowSaveForm,
  setShowSaveForm: propSetShowSaveForm,
  onProjectUpdated // Add new prop
}) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  // Use props for showSaveForm if provided, otherwise use local state
  const [localShowSaveForm, setLocalShowSaveForm] = useState(false);
  const showSaveForm = propShowSaveForm !== undefined ? propShowSaveForm : localShowSaveForm;
  const setShowSaveForm = propSetShowSaveForm || setLocalShowSaveForm;
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [localBackups, setLocalBackups] = useState([]);

  // Log calculatorState when save form is shown
  useEffect(() => {
    if (showSaveForm) {
      console.log('[ProjectManager DEBUG] Save form is now visible. Current calculatorState:', calculatorState);
    }
  }, [showSaveForm, calculatorState]);

  // Fetch user's projects
  useEffect(() => {
    let isMounted = true;
    
    const loadProjects = async () => {
      if (!user) {
        console.log('[ProjectManager DEBUG] useEffect: No user detected, setting loading to false.');
        setLoading(false);
        setProjects([]);
        return;
      }
      
      console.log('[ProjectManager DEBUG] useEffect: User detected, setting loading to true.');
      setLoading(true);
      
      try {
        console.log('[ProjectManager DEBUG] useEffect: About to call fetchProjects.');
        await fetchProjects();
        console.log('[ProjectManager DEBUG] useEffect: fetchProjects call completed.');
      } catch (error) {
        console.error('[ProjectManager DEBUG] useEffect: Error caught after fetchProjects call:', error);
        if (isMounted) {
          console.log('[ProjectManager DEBUG] useEffect: Error caught, setting loading to false.');
          setLoading(false);
        }
      }
      // Note: setLoading(false) is primarily handled by fetchProjects' finally block.
    };
    
    loadProjects();
    
    return () => {
      console.log('[ProjectManager DEBUG] useEffect: Cleanup function run.');
      isMounted = false;
    };
  }, [user]); // Only re-run when user changes

  // Load local backups on mount
  useEffect(() => {
    const savedBackups = localStorage.getItem('backupProjects');
    if (savedBackups) {
      try {
        const parsedBackups = JSON.parse(savedBackups);
        setLocalBackups(parsedBackups);
      } catch (error) {
        console.error('Error loading local backups:', error);
      }
    }
  }, []);

  const fetchProjects = async () => {
    if (!user) {
      console.log('[ProjectManager DEBUG] fetchProjects: No user, returning.');
      return; // Should not happen if useEffect guard is working
    }
    
    // setLoading(true); // Moved to the useEffect to avoid potential rapid toggles if fetchProjects is called elsewhere
    console.log('[ProjectManager DEBUG] fetchProjects: Starting for user:', user.id);
    
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, created_at, updated_at, data, description')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('[ProjectManager DEBUG] fetchProjects: Supabase error fetching projects:', error);
        if (error.code === '42P01') {
          console.log('[ProjectManager DEBUG] fetchProjects: Projects table may not exist yet (42P01).');
        }
        setProjects([]); // Ensure projects is empty on error
        // setLoading(false); // Let finally handle this
        return; // Exit if there's an error
      } else {
        console.log(`[ProjectManager DEBUG] fetchProjects: Successfully fetched ${data?.length || 0} projects.`);
        setProjects(data || []);
      }
    } catch (error) {
      console.error('[ProjectManager DEBUG] fetchProjects: CRITICAL ERROR fetching projects:', error);
      setProjects([]); // Ensure projects is empty on critical error
      // setLoading(false); // Let finally handle this
    } finally {
      console.log('[ProjectManager DEBUG] fetchProjects: Finally block reached, setting loading to false.');
      setLoading(false); // Ensure loading is always set to false after fetch attempt
    }
  };

  const handleSaveProject = async (e) => {
    e.preventDefault();
    console.log('[ProjectManager DEBUG] handleSaveProject: Form submitted. currentProjectId:', currentProjectId, 'Project Name:', projectName); 
    if (!user) {
      console.warn('[ProjectManager DEBUG] handleSaveProject: No user logged in.');
      onProjectAction('save', 'You must be logged in to save projects');
      return;
    }
    
    if (!projectName.trim()) {
      console.warn('[ProjectManager DEBUG] handleSaveProject: Project name is empty.');
      onProjectAction('save', 'Please enter a project name');
      return;
    }
    
    console.log('[ProjectManager DEBUG] handleSaveProject: Setting saving to true.');
    setSaving(true);
    
    try {
      // DEBUG: Log the calculatorState received by the save function
      console.log('[ProjectManager DEBUG] handleSaveProject: calculatorState received:', JSON.parse(JSON.stringify(calculatorState)));

      // Prepare the project data
      const projectData = {
        user_id: user.id,
        name: projectName.trim(),
        description: projectDescription.trim(),
        data: JSON.stringify(calculatorState),
        updated_at: new Date().toISOString(),
      };
      
      if (!currentProjectId) {
        projectData.created_at = new Date().toISOString();
      }
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Save request timed out after 20 seconds')), 20000);
      });
      
      let dbPromise;
      if (currentProjectId) {
        // Update existing project
        dbPromise = supabase
          .from('projects')
          .update(projectData)
          .eq('id', currentProjectId)
          .eq('user_id', user.id)
          .select();
      } else {
        // Create new project
        dbPromise = supabase
          .from('projects')
          .insert([projectData])
          .select();
      }
      
      // Race the database operation against the timeout
      const result = await Promise.race([
        dbPromise,
        timeoutPromise.then(() => ({ data: null, error: new Error('Save request timed out after 20 seconds') }))
      ]);
      
      const { data, error } = result;
      
      console.log('[ProjectManager DEBUG] Promise.race full result:', JSON.stringify(result, null, 2));
      console.log('[ProjectManager DEBUG] Extracted data from Promise.race result:', JSON.stringify(data, null, 2));
      console.log('[ProjectManager DEBUG] Extracted error from Promise.race result:', error); // Log error directly, it might be an Error object

      if (error) {
        console.error('[ProjectManager DEBUG] handleSaveProject: Supabase error during save/update (from if(error) block):', error);
        throw error;
      }
      
      if (data && data.length > 0) {
        console.log('[ProjectManager DEBUG] handleSaveProject: Save/update successful in DB.');
        if (currentProjectId) {
          setProjects(prevProjects => 
            prevProjects.map(p => p.id === currentProjectId ? data[0] : p)
          );
          window.alert('Project updated successfully!'); // User confirmation
          if (onProjectUpdated) {
            onProjectUpdated(); // Call the new callback
          }
        } else {
          setProjects(prevProjects => [data[0], ...prevProjects]);
          onProjectAction('save', 'Project saved successfully');
        }
        
        // Reset the form
        setProjectName('');
        setProjectDescription('');
        setCurrentProjectId(null);
        setShowSaveForm(false);
        
        // Refresh the projects list with the newly saved project
        try {
          await fetchProjects();
          console.log('Projects refreshed after save');
        } catch (err) {
          console.log('Project refresh failed, but save was successful:', err.message);
        }
      } else {
        console.log('[ProjectManager DEBUG] handleSaveProject: Save/update failed or returned no data.');
        // Save to localStorage as a backup
        saveToLocalStorage(projectData);
      }
    } catch (error) {
      console.error('[ProjectManager DEBUG] handleSaveProject: Error during save attempt:', error.message);
      // Try to save to localStorage as a last resort
      try {
        const projectData = {
          user_id: user.id,
          name: projectName.trim(),
          description: projectDescription.trim(),
          data: JSON.stringify(calculatorState),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        
        saveToLocalStorage(projectData);
        
        // Reset the form
        setProjectName('');
        setProjectDescription('');
        setCurrentProjectId(null);
        setShowSaveForm(false);
      } catch (localError) {
        console.error('Even localStorage save failed:', localError);
      }
    } finally {
      console.log('[ProjectManager DEBUG] handleSaveProject: In finally block, setting saving to false.');
      setSaving(false);
    }
  };
  
  const handleEditProjectClick = (projectToEdit) => {
    console.log('[ProjectManager DEBUG] handleEditProjectClick called for project:', projectToEdit.id, projectToEdit.name);
    setProjectName(projectToEdit.name);
    setProjectDescription(projectToEdit.description || '');
    setCurrentProjectId(projectToEdit.id);
    setShowSaveForm(true);
  };

  // Helper function to save project to localStorage as a backup
  const saveToLocalStorage = (projectData) => {
    try {
      const backups = JSON.parse(localStorage.getItem('backupProjects') || '[]');
      const newBackup = {
        ...projectData,
        id: `local-${Date.now()}`,
      };
      backups.unshift(newBackup);
      localStorage.setItem('backupProjects', JSON.stringify(backups));
      setLocalBackups(backups);
      onProjectAction('save', 'Project saved locally (offline mode)');
    } catch (e) {
      console.error('Error saving to localStorage:', e);
      onProjectAction('save', 'Error: Could not save project');
    }
  };

  // Handle direct project update without showing form
  const handleUpdateProject = async (project) => {
    if (!user) {
      onProjectAction('update', 'You must be logged in to update projects');
      return;
    }
    
    setSaving(true);
    
    try {
      // Prepare the project data - keep the same name and description
      const projectData = {
        data: JSON.stringify(calculatorState),
        updated_at: new Date().toISOString(),
      };
      
      // Update existing project
      const { data, error } = await supabase
        .from('projects')
        .update(projectData)
        .eq('id', project.id)
        .eq('user_id', user.id)
        .select();
      
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        // Successfully updated in database
        setProjects(prevProjects => 
          prevProjects.map(p => p.id === project.id ? data[0] : p)
        );
        onProjectAction('update', `Project "${project.name}" updated successfully`);
      }
    } catch (error) {
      console.error('Error updating project:', error);
      onProjectAction('update', `Error updating project: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle loading a project
  const handleLoadProject = async (project) => {
    if (!user) return;
    
    setLoading(true);
    
    try {
      // Handle loading project data
      if (project && typeof project === 'object') {
        // If it's a local backup with isLocalBackup flag
        if (project.isLocalBackup) {
          try {
            // Parse the data if it's a string
            const projectData = typeof project.data === 'string' ? JSON.parse(project.data) : project.data;
            
            // Load the project data into the calculator
            onLoadProject(projectData);
            onProjectAction('load', `Loaded local backup: ${project.name}`);
            
            // Also try to save it to the database in the background
            const { error } = await supabase
              .from('projects')
              .insert([{
                user_id: user.id,
                name: project.name,
                description: project.description || '',
                data: typeof project.data === 'string' ? project.data : JSON.stringify(project.data),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }]);
              
            if (!error) {
              // Successfully synced to cloud
              onProjectAction('sync', `Synced "${project.name}" to cloud storage`);
              
              // Remove from localStorage
              const updatedBackups = localBackups.filter(p => p.id !== project.id);
              localStorage.setItem('backupProjects', JSON.stringify(updatedBackups));
              setLocalBackups(updatedBackups);
            }
          } catch (error) {
            console.error('Error loading local backup:', error);
            onProjectAction('load', `Error loading backup: ${error.message}`);
          }
        } else {
          // Regular project from database
          try {
            // Parse the data if it's a string
            const projectData = typeof project.data === 'string' ? JSON.parse(project.data) : project.data;
            
            // Load the project data into the calculator
            onLoadProject(projectData);
            onProjectAction('load', `Loaded project: ${project.name}`);
          } catch (error) {
            console.error('Error loading project:', error);
            onProjectAction('load', `Error loading project: ${error.message}`);
          }
        }
      } else if (typeof project === 'string') {
        // If we received a project ID instead of a project object
        try {
          // Create a timeout promise
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Request timed out after 15 seconds')), 15000);
          });
          
          // Create the Supabase query promise
          console.log('Creating SQL query for project...');
          const startTime = Date.now();
          const queryPromise = supabase
            .from('projects')
            .select('*')
            .eq('id', project)
            .eq('user_id', user.id)
            .limit(1);
          
          // Race the query against the timeout
          const result = await Promise.race([
            queryPromise,
            timeoutPromise.then(() => ({ data: null, error: new Error('Request timed out after 15 seconds') }))
          ]);
          
          const { data, error } = result;
          
          if (error) {
            throw error;
          }
          
          if (data && data.length > 0) {
            // Parse the data if it's a string
            let projectData;
            try {
              projectData = typeof data[0].data === 'string' ? JSON.parse(data[0].data) : data[0].data;
              if (!projectData || typeof projectData !== 'object') {
                throw new Error('Invalid project data format');
              }
            } catch (e) {
              console.error('Error parsing project data:', e);
              onProjectAction('load', 'Error: Could not load project data');
              return;
            }
            
            onLoadProject(projectData);
            
            // Notify parent component that a project was loaded
            onProjectAction('load', `Loaded project: ${data[0].name}`);
          } else {
            throw new Error('Project not found');
          }
        } catch (error) {
          console.error('Error loading project by ID:', error);
          onProjectAction('load', `Error: ${error.message}`);
        }
      } else {
        console.error('Invalid project data received:', project);
      }
    } catch (error) {
      setLoading(false);
      // Try to load from localStorage as a fallback
      if (typeof project === 'string' && !project.startsWith('local-')) {
        try {
          const backups = JSON.parse(localStorage.getItem('backupProjects') || '[]');
          // Try to find a backup with a similar name
          const matchingBackups = backups.filter(p => p.name.toLowerCase().includes(project.toLowerCase()));
          
          if (matchingBackups.length > 0) {
            handleLoadProject(matchingBackups[0]);
          } else {
            onProjectAction('load', 'Error: Project not found in local backups');
          }
        } catch (e) {
          console.error('Error loading from local storage:', e);
          onProjectAction('load', 'Error: Could not load project');
        }
      } else {
        onProjectAction('load', `Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a project
  const handleDeleteProject = async (projectId) => {
    if (!user) {
      onProjectAction('delete', 'You must be logged in to delete projects');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }
    
    setDeleting(true);
    console.log('Deleting project:', projectId);
    
    try {
      console.log('Using direct SQL delete via Supabase client');
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Delete request timed out after 15 seconds')), 15000);
      });
      
      // Create the Supabase delete promise
      console.log('Creating SQL delete for project...');
      const startTime = Date.now();
      const deletePromise = supabase
        .from('projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', user.id);
      
      // Race the delete operation against the timeout
      const result = await Promise.race([
        deletePromise,
        timeoutPromise.then(() => ({ data: null, error: new Error('Delete request timed out after 15 seconds') }))
      ]);
      
      const { error } = result;
      
      if (error) {
        throw error;
      }
      
      console.log(`Delete completed in ${Date.now() - startTime}ms`);
      
      // Update local state
      setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));
      
      // Notify parent component that a project was deleted
      onProjectAction('delete', 'Project deleted successfully');
    } catch (error) {
      console.error('Error deleting project:', error);
      onProjectAction('delete', `Error deleting project: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="project-manager">
      <div className="project-header">
        <button 
          className="quick-save-button" 
          onClick={() => setShowSaveForm(true)}
          disabled={!user || saving}
        >
          Save New Project
        </button>
      </div>

      <div className="project-content">
          {showSaveForm && (
            <form onSubmit={handleSaveProject} className="project-form">
              <div className="form-group">
                <label htmlFor="projectName">Project Name</label>
                <input
                  id="projectName"
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="projectDescription">Description (optional)</label>
                <textarea
                  id="projectDescription"
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                />
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  className="project-button secondary"
                  onClick={() => setShowSaveForm(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="project-button primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Project'}
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading projects...</div>
            </div>
          ) : (
            <div className="project-list">
              {/* Project List */}
              {projects && projects.length > 0 && (
                <>
                  <h3 className="projects-section-title">Your Projects</h3>
                  {projects.map(project => (
                    <div key={project.id} className="project-item">
                      <div className="project-info">
                        <h3>{project.name}</h3>
                        {project.description && <p>{project.description}</p>}
                        <div className="project-meta">
                          <span>Created: {new Date(project.created_at).toLocaleDateString()}</span>
                          {project.updated_at && (
                            <span>Updated: {new Date(project.updated_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="project-actions">
                        <button 
                          onClick={() => handleLoadProject(project)}
                          disabled={loading}
                        >
                          Load
                        </button>
                        <button 
                          onClick={() => handleEditProjectClick(project)}
                          disabled={saving} 
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteProject(project.id)}
                          disabled={deleting}
                          className="delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Local Backups */}
              {localBackups.length > 0 && (
                <>
                  <h3 className="projects-section-title">Local Backups</h3>
                  <div className="backup-info">
                    These are projects saved locally when the database connection was unavailable.
                    Loading them will attempt to sync them to your cloud storage.
                  </div>
                  {localBackups.map(backup => (
                    <div key={backup.id} className="project-item backup">
                      <div className="project-info">
                        <h3>{backup.name}</h3>
                        {backup.description && <p>{backup.description}</p>}
                        <div className="project-meta">
                          <span>Created: {new Date(backup.created_at).toLocaleDateString()}</span>
                          <span className="backup-badge">Local Backup</span>
                        </div>
                      </div>
                      <div className="project-actions">
                        <button 
                          onClick={() => handleLoadProject({...backup, isLocalBackup: true})}
                          disabled={loading}
                        >
                          Load & Sync
                        </button>
                        <button 
                          onClick={() => {
                            // Remove from localStorage
                            const updatedBackups = localBackups.filter(p => p.id !== backup.id);
                            localStorage.setItem('backupProjects', JSON.stringify(updatedBackups));
                            setLocalBackups(updatedBackups);
                          }}
                          disabled={deleting}
                          className="delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* No Projects */}
              {projects.length === 0 && localBackups.length === 0 && (
                <div className="no-projects">
                  No saved projects. Create one by saving your current settings.
                </div>
              )}
            </div>
          )}
        </div>
    </div>
  );
};

export default ProjectManager;
